"use client";

import AppLink from '@/app/components/AppLink';
import Link from 'next/link'
import { useState, useEffect } from 'react'
import {
  FiMenu,
  FiX,
  FiUser,
  FiGrid
} from 'react-icons/fi'
import { DEFAULT_LOCALE, getSiteCopy, localePath, normalizeLocale } from '@/lib/siteI18n'
import { useLandingAccount } from '../../lib/landingAccount'
import LanguageSwitcher from './LanguageSwitcher'

export default function Header({ locale = DEFAULT_LOCALE }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const activeLocale = normalizeLocale(locale)
  const copy = getSiteCopy(activeLocale).nav
  const account = useLandingAccount()
  const guest = account?.kind === 'guest'
  const workspaceUrl = `/app/dashboard?lang=${activeLocale}`

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''} ${account ? 'header--session' : ''}`} data-account-state={account?.kind || 'visitor'}>
      <div className="nav-container">
        <div className="logo">
          <Link href={localePath('/', activeLocale)} className="brand" aria-label={copy.homeLabel}>
            <img src="/logo.svg" className="brand__icon" alt="" />
            <span className="brand__text">
              <span className="brand__name">
                Instant<span className="brand__name--grad">Transcriber</span>
              </span>
              <span className="brand__tagline">{copy.tagline}</span>
            </span>
          </Link>
        </div>
        <nav id="site-navigation" className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
          <Link href={localePath('/features', activeLocale)}>{copy.features}</Link>
          <Link href={localePath('/tools', activeLocale)}>{copy.tools}</Link>
          <Link href={localePath('/#how-it-works', activeLocale)}>{copy.howItWorks}</Link>
          <Link href={localePath('/#pricing', activeLocale)}>{copy.pricing}</Link>
          <Link href={localePath('/use-cases', activeLocale)}>{copy.useCases}</Link>
          <Link href={localePath('/blog', activeLocale)}>{copy.blog}</Link>
          <LanguageSwitcher locale={activeLocale} label={copy.language} compact />
          {account ? <div className="nav-session">
            {guest ? <AppLink href={`/app/signup?lang=${activeLocale}`} className="nav-account nav-account--guest"
              onClick={() => setMobileMenuOpen(false)} data-analytics-cta-id="header_complete_signup" data-analytics-cta-location="header">
              <FiUser aria-hidden="true" /><span><small>{copy.guest}</small>{copy.completeSignup}</span>
            </AppLink> : <AppLink href={`/app/dashboard/profile?lang=${activeLocale}`} className="nav-account"
              title={account.email || copy.account} aria-label={copy.account} onClick={() => setMobileMenuOpen(false)}
              data-analytics-cta-id="header_account" data-analytics-cta-location="header">
              <FiUser aria-hidden="true" /><span>{account.email || copy.account}</span>
            </AppLink>}
            <AppLink href={workspaceUrl} className="nav-cta" onClick={() => setMobileMenuOpen(false)}
              data-analytics-cta-id="header_my_transcripts" data-analytics-cta-location="header">{copy.workspace}</AppLink>
          </div> : <>
            <AppLink href="/app/login" className="nav-login">{copy.login}</AppLink>
            <AppLink href="/app/signup" className="nav-cta" onClick={() => setMobileMenuOpen(false)}>{copy.signup}</AppLink>
          </>}
        </nav>
        {account && <AppLink href={workspaceUrl} className="nav-mobile-workspace" aria-label={copy.workspace}
          data-analytics-cta-id="header_mobile_workspace" data-analytics-cta-location="header"><FiGrid aria-hidden="true" /></AppLink>}
        <button
          className="mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={copy.toggleMenu}
          aria-expanded={mobileMenuOpen}
          aria-controls="site-navigation"
        >
          {mobileMenuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>
    </header>
  )
}
